import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const PUBLIC = process.env.PUBLIC_URL || "";

const hotelPartners = [
  {
    name: "Novotel London Excel",
    titleAttr: "Novotel London Excel – Hotel Laundry Partner",
    altText:
      "Novotel London Excel logo, guest and staff laundry partner of Xcel Dry Cleaners",
    caption: "Same-day guest & staff laundry",
    slug: "/case-studies/novotel-london-excel-laundry-partnership",
    logo: `${PUBLIC}/images/hotels/novotel-logo.jpg`,
    logoAlt: "Novotel Hotels & Resorts logo",
    photo: `${PUBLIC}/images/hotels/novotel-hotel.webp`,
    photoAlt: "Novotel London Excel hotel exterior",
    fallbackLabel: "NOVOTEL",
    fallbackSub: "HOTELS & RESORTS",
  },
  {
    name: "Crowne Plaza London Docklands by IHG",
    titleAttr: "Crowne Plaza London Docklands by IHG – Hotel Laundry Partner",
    altText:
      "Crowne Plaza London Docklands by IHG logo, guest and staff laundry partner of Xcel Dry Cleaners",
    caption: "Same-day guest & staff laundry",
    slug: "/case-studies/crowne-plaza-london-docklands-laundry-partnership",
    logo: `${PUBLIC}/images/hotels/crowne-plaza-logo.svg.webp`,
    logoAlt: "Crowne Plaza Hotels & Resorts logo",
    photo: `${PUBLIC}/images/hotels/crowne-plaza-hotel.webp`,
    photoAlt: "Crowne Plaza London Docklands hotel exterior at night",
    fallbackLabel: "CROWNE PLAZA",
    fallbackSub: "HOTELS & RESORTS",
  },
  {
    name: "ibis London Excel Docklands",
    titleAttr: "ibis London Excel Docklands – Hotel Laundry Partner",
    altText:
      "ibis London Excel Docklands logo, guest and staff laundry partner of Xcel Dry Cleaners",
    caption: "Same-day guest & staff laundry",
    slug: "/case-studies/ibis-london-excel-docklands-laundry-partnership",
    logo: `${PUBLIC}/images/hotels/ibis-logo.jpg`,
    logoAlt: "ibis Hotels logo",
    photo: `${PUBLIC}/images/hotels/ibis-hotel.webp`,
    photoAlt: "ibis London Excel Docklands hotel exterior",
    fallbackLabel: "ibis",
    fallbackSub: "",
  },
];

type Partner = (typeof hotelPartners)[0];

function HotelCard({ partner }: { partner: Partner }) {
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  return (
    <div className="xcel-partner-card">
      <Link
        to={partner.slug}
        title={partner.titleAttr}
        className="xcel-partner-link"
      >
        {/* Photo area */}
        <div className="xcel-partner-photo-wrap">
          <img
            src={partner.photo}
            alt={partner.photoAlt}
            className={`xcel-partner-photo${photoLoaded ? " loaded" : ""}`}
            onLoad={() => setPhotoLoaded(true)}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <div className="xcel-partner-photo-overlay" />

          {/* Logo badge on photo */}
          <div className="xcel-partner-logo-badge" aria-label={partner.altText}>
            <img
              src={partner.logo}
              alt={partner.logoAlt}
              className={`xcel-partner-logo-img${logoLoaded ? " loaded" : ""}`}
              onLoad={() => setLogoLoaded(true)}
              onError={(e) => {
                const el = e.target as HTMLImageElement;
                el.style.display = "none";
                const fb = el.nextElementSibling as HTMLElement | null;
                if (fb) fb.style.display = "flex";
              }}
            />
            {/* Text fallback shown only when logo fails to load */}
            <span
              className="xcel-partner-logo-fallback"
              style={{ display: "none" }}
            >
              <strong>{partner.fallbackLabel}</strong>
              {partner.fallbackSub && <small>{partner.fallbackSub}</small>}
            </span>
          </div>
        </div>

        {/* Card footer */}
        <div className="xcel-partner-card-footer">
          <p className="xcel-partner-name">{partner.name}</p>
          <p className="xcel-partner-caption">{partner.caption}</p>
          <span className="xcel-partner-cta-link">
            Read case study <span aria-hidden="true">→</span>
          </span>
        </div>
      </Link>
    </div>
  );
}

function HotelPartnersCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabHidden = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const startAutoplay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!tabHidden.current) {
        setCurrentSlide((prev) => (prev + 1) % hotelPartners.length);
      }
    }, 4500);
  };

  const stopAutoplay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isMobile) startAutoplay();
    else stopAutoplay();

    const onVisibility = () => {
      tabHidden.current = document.hidden;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopAutoplay();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const goTo = (index: number) => {
    setCurrentSlide(index);
    if (isMobile) startAutoplay();
  };

  const prev = () =>
    goTo((currentSlide - 1 + hotelPartners.length) % hotelPartners.length);
  const next = () => goTo((currentSlide + 1) % hotelPartners.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <section
      className="xcel-hotel-partners-section"
      aria-label="Hotel partners"
      onMouseEnter={stopAutoplay}
      onMouseLeave={() => isMobile && startAutoplay()}
      onFocus={stopAutoplay}
      onBlur={() => isMobile && startAutoplay()}
    >
      <div className="container">
        {/* Heading */}
        <div className="xcel-partners-heading text-center">
          <p className="xcel-partners-eyebrow">
            In partnership with London's leading hotels
          </p>
          <h2>Trusted by Leading London Hotels</h2>
          <p className="xcel-partners-sub">
            100% guest & staff satisfaction across all partner hotels
          </p>
        </div>

        {/* Desktop: 3 cards */}
        <div className="xcel-partners-desktop">
          {hotelPartners.map((p, i) => (
            <HotelCard key={i} partner={p} />
          ))}
        </div>

        {/* Mobile: swipeable */}
        <div
          className="xcel-partners-mobile"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          aria-live="polite"
        >
          <HotelCard partner={hotelPartners[currentSlide]} />

          <button
            className="xcel-carousel-arrow xcel-carousel-arrow--prev"
            onClick={prev}
            aria-label="Previous hotel partner"
          >
            &#8249;
          </button>
          <button
            className="xcel-carousel-arrow xcel-carousel-arrow--next"
            onClick={next}
            aria-label="Next hotel partner"
          >
            &#8250;
          </button>

          <div className="xcel-carousel-dots" role="tablist">
            {hotelPartners.map((p, i) => (
              <button
                key={i}
                className={`xcel-carousel-dot${i === currentSlide ? " active" : ""}`}
                onClick={() => goTo(i)}
                role="tab"
                aria-selected={i === currentSlide}
                aria-label={`Go to ${p.name}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom trust stat */}
      </div>
    </section>
  );
}

export default HotelPartnersCarousel;
